// src/components/layout/Navbar.js - Updated with Clerk integration
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser, UserButton, useClerk } from '@clerk/clerk-react';
import {
  FaBars,
  FaTimes,
  FaUser,
  FaSignOutAlt,
  FaCog,
  FaHome,
  FaInfoCircle,
  FaGraduationCap,
  FaImages,
  FaEnvelope,
  FaUserPlus,
  FaSignInAlt,
  FaTachometerAlt,
  FaChevronDown,
  FaBell,
  FaTrophy,
  FaPlus,
  FaBullseye,
  FaCalendarAlt,
  FaEye,
  FaUsers,
  FaUserShield // Added for admin icon
} from 'react-icons/fa';
import logo from '../../assets/images/debaters.jpg';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const { user, isSignedIn, isLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is super admin via Clerk public metadata or email
  const SUPERADMIN_EMAILS = ['info@africandebatechampions.org', 'africandebatechampions@gmail.com'];
  const isSuperAdmin =
    user?.publicMetadata?.role === 'admin' ||
    user?.publicMetadata?.isAdmin === true ||
    SUPERADMIN_EMAILS.includes(user?.primaryEmailAddress?.emailAddress);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset menus on route change
  useEffect(() => {
    setIsOpen(false);
    setShowUserMenu(false);
    setShowAboutDropdown(false);
  }, [location]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-container')) {
        setShowAboutDropdown(false);
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const getAvatarUrl = (clerkUser) => {
    // Use Clerk's profile image if available
    if (clerkUser?.imageUrl) {
      return clerkUser.imageUrl;
    }
    // Otherwise generate initials avatar
    if (clerkUser?.fullName) {
      const initials = clerkUser.fullName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=22c55e&color=ffffff&size=80&rounded=true&font-size=0.33`;
    }
    // Fallback to first name or email
    const name = clerkUser?.firstName || clerkUser?.primaryEmailAddress?.emailAddress?.charAt(0) || 'U';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=22c55e&color=ffffff&size=80&rounded=true&font-size=0.33`;
  };

  const aboutDropdownItems = [
    { name: 'About Us', path: '/about', icon: FaInfoCircle, description: 'Learn about our story' },
    { name: 'Our Mission', path: '/about/mission', icon: FaBullseye, description: 'Our purpose and goals' },
    { name: 'Our Vision', path: '/about/vision', icon: FaEye, description: 'Where we\'re heading' },
    { name: 'Our Team', path: '/about/team', icon: FaUsers, description: 'Meet the people behind ACD' },
  ];

  const navItems = [
    { name: 'Home', path: '/', icon: FaHome },
    { name: 'About', path: '/about', icon: FaInfoCircle, hasDropdown: true, dropdownItems: aboutDropdownItems },
    { name: 'Programs', path: '/programs', icon: FaGraduationCap },
    { name: 'Events', path: '/events', icon: FaCalendarAlt },
    { name: 'Gallery', path: '/gallery', icon: FaImages },
    { name: 'Contact', path: '/contact', icon: FaEnvelope },
  ];

  return (
    <div className="fixed w-full top-0 z-50">
      {/* Mobile: Full width, Desktop: U-shaped container with rounded bottom corners */}
      <div className="relative w-full lg:flex lg:justify-center">
        <div
          className={`relative w-full lg:w-[85%] lg:max-w-5xl h-16 transition-all duration-300 lg:rounded-b-[2rem] ${
            scrolled 
              ? 'bg-gray-900/95 shadow-2xl' 
              : 'bg-gray-900/90'
          }`}
          style={{
            backdropFilter: 'blur(12px)',
            boxShadow: scrolled 
              ? '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 0 0 1px rgba(255, 255, 255, 0.1)' 
              : '0 4px 16px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.05)',
          }}
        >
          {/* Navigation content inside U-shape */}
          <div className="flex items-center justify-between h-16 px-6">
            {/* Left - Logo with company name */}
            <Link to="/" className="flex items-center space-x-3 group">
              <motion.div
                className="relative"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <img
                  src={logo}
                  alt="The African Child Debaters Champions Ltd"
                  className={`object-cover rounded-full border-3 border-yellow-400 transition-all duration-300 ${
                    scrolled ? 'w-10 h-10' : 'w-12 h-12'
                  }`}
                  style={{
                    boxShadow: `
                      0 0 20px rgba(251, 191, 36, 0.6),
                      0 0 40px rgba(251, 191, 36, 0.3),
                      0 4px 15px rgba(0, 0, 0, 0.3)
                    `
                  }}
                />
                {/* Glowing ring effect */}
                <div 
                  className="absolute inset-0 rounded-full border-2 border-yellow-300/50 animate-pulse"
                  style={{
                    boxShadow: '0 0 25px rgba(251, 191, 36, 0.4)'
                  }}
                />
              </motion.div>
              <div className="block">
                {/* Desktop: Show full text */}
                <div className="hidden sm:block">
                  <div className="text-white font-bold text-sm lg:text-base">
                    The ACDC Ltd
                  </div>
                  <div className="text-yellow-300 text-xs -mt-1">
                    Empowering African Child Voices
                  </div>
                </div>
                {/* Mobile: Show only company name */}
                <div className="sm:hidden">
                  <div className="text-white font-bold text-sm">
                    The ACDC Ltd
                  </div>
                </div>
              </div>
            </Link>

            {/* Center - Navigation items */}
            <div className="hidden lg:flex items-center space-x-4">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path ||
                  (item.hasDropdown && item.dropdownItems?.some(sub => location.pathname === sub.path));

                if (item.hasDropdown) {
                  return (
                    <div key={item.name} className="relative dropdown-container">
                      <button
                        onClick={() => setShowAboutDropdown(!showAboutDropdown)}
                        onMouseEnter={() => setShowAboutDropdown(true)}
                        className={`px-3 py-2 rounded-full font-medium flex items-center space-x-2 transition-all duration-200 text-sm ${
                          isActive || showAboutDropdown
                            ? 'bg-yellow-500/20 text-yellow-300'
                            : 'text-white hover:text-yellow-300 hover:bg-white/10'
                        }`}
                      >
                        <span>{item.name}</span>
                        <FaChevronDown className={`w-2 h-2 transition-transform ${showAboutDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      <AnimatePresence>
                        {showAboutDropdown && (
                          <motion.div
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 5 }}
                            transition={{ duration: 0.15 }}
                            className="absolute left-0 w-64 bg-gray-800/95 backdrop-blur-md shadow-xl border border-gray-700/50 py-2 overflow-hidden"
                            onMouseLeave={() => setShowAboutDropdown(false)}
                            style={{
                              marginTop: '0px',
                              borderTop: 'none'
                            }}
                          >
                            {aboutDropdownItems.map((subItem) => {
                              const SubIcon = subItem.icon;
                              const isSubActive = location.pathname === subItem.path;
                              return (
                                <Link
                                  key={subItem.name}
                                  to={subItem.path}
                                  className={`flex items-start space-x-3 px-4 py-3 transition-all duration-150 ${
                                    isSubActive
                                      ? 'bg-yellow-500/20 text-yellow-300 border-l-4 border-yellow-500'
                                      : 'text-gray-300 hover:text-yellow-300 hover:bg-white/10'
                                  }`}
                                  onClick={() => setShowAboutDropdown(false)}
                                >
                                  <div className="flex-shrink-0 mt-0.5">
                                    <SubIcon className={`w-3 h-3 ${isSubActive ? 'text-yellow-300' : 'text-gray-400'}`} />
                                  </div>
                                  <div>
                                    <div className="font-medium text-xs">{subItem.name}</div>
                                    <div className="text-xs text-gray-400 mt-1">{subItem.description}</div>
                                  </div>
                                </Link>
                              );
                            })}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`px-3 py-2 rounded-full font-medium transition-all duration-200 text-sm ${
                      isActive
                        ? 'bg-yellow-500/20 text-yellow-300'
                        : 'text-white hover:text-yellow-300 hover:bg-white/10'
                    }`}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Right - Auth section */}
            <div className="flex items-center space-x-3">
              {isSignedIn ? (
                <div className="flex items-center space-x-3">
                  {/* Dashboard Link */}
                  <Link
                    to="/dashboard"
                    className="hidden sm:flex items-center space-x-2 px-3 py-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all duration-200 text-white text-xs font-medium"
                  >
                    <FaTachometerAlt className="w-3 h-3 text-yellow-400" />
                    <span>Dashboard</span>
                  </Link>

                  {/* Admin Link - Only show for super admin */}
                  {isSuperAdmin && (
                    <Link
                      to="/admin"
                      className="hidden sm:flex items-center space-x-2 px-3 py-2 rounded-full bg-purple-500/20 backdrop-blur-md hover:bg-purple-500/30 transition-all duration-200 text-purple-300 text-xs font-medium"
                    >
                      <FaUserShield className="w-3 h-3" />
                      <span>Admin</span>
                    </Link>
                  )}

                  {/* Clerk's UserButton - handles profile, settings, and logout */}
                  <div className="flex items-center">
                    <UserButton
                      afterSignOutUrl="/"
                      appearance={{
                        elements: {
                          avatarBox: "w-8 h-8 rounded-full border-2 border-yellow-400/50",
                          userButtonPopoverCard: "bg-gray-800 border border-gray-700",
                          userButtonPopoverActionButton: "hover:bg-white/10",
                          userButtonPopoverActionButtonText: "text-gray-300",
                          userButtonPopoverActionButtonIcon: "text-yellow-400",
                          userButtonPopoverFooter: "hidden"
                        }
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="hidden lg:flex items-center space-x-3">
                  <Link 
                    to="/login" 
                    className="px-4 py-2 text-white hover:text-yellow-300 font-medium transition-colors text-sm"
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-full font-medium hover:bg-yellow-400 transition-all duration-200 shadow-lg hover:shadow-xl text-sm"
                  >
                    Register
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              <motion.button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden p-2 rounded-lg bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isOpen ? <FaTimes className="w-4 h-4" /> : <FaBars className="w-4 h-4" />}
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-gray-800/95 backdrop-blur-md border-t border-gray-700/50 mt-2 mx-4 overflow-hidden rounded-xl"
          >
            <div className="px-4 py-4 space-y-3">
              {/* Mobile About dropdown */}
              <div>
                <button
                  onClick={() => setShowAboutDropdown(!showAboutDropdown)}
                  className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-gray-300 hover:text-yellow-300 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <FaInfoCircle className="w-4 h-4 text-yellow-400" />
                    <span className="font-medium text-sm">About</span>
                  </div>
                  <FaChevronDown className={`w-3 h-3 transition-transform ${showAboutDropdown ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {showAboutDropdown && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 mt-2 space-y-1 border-l-2 border-yellow-400/30 pl-4"
                    >
                      {aboutDropdownItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.path}
                            className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-400 hover:text-yellow-300 hover:bg-white/10 transition-colors text-sm"
                            onClick={() => {
                              setIsOpen(false);
                              setShowAboutDropdown(false);
                            }}
                          >
                            <SubIcon className="w-3 h-3 text-yellow-400" />
                            <span>{subItem.name}</span>
                          </Link>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Other mobile nav items */}
              {navItems.filter(item => !item.hasDropdown).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                      isActive 
                        ? 'bg-yellow-500/20 text-yellow-300' 
                        : 'text-gray-300 hover:text-yellow-300 hover:bg-white/10'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <Icon className="w-4 h-4 text-yellow-400" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                );
              })}

              {/* Mobile auth items */}
              {!isSignedIn && (
                <div className="border-t border-gray-700/50 pt-3 mt-3 space-y-2">
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-gray-300 hover:text-yellow-300 hover:bg-white/10 rounded-lg text-center transition-colors text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="block px-3 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 text-center font-medium transition-colors text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    Register
                  </Link>
                </div>
              )}
              
              {isSignedIn && (
                <div className="border-t border-gray-700/50 pt-3 mt-3">
                  <Link
                    to="/dashboard"
                    className="flex items-center space-x-3 px-3 py-2 text-gray-300 hover:text-yellow-300 hover:bg-white/10 rounded-lg transition-colors text-sm"
                    onClick={() => setIsOpen(false)}
                  >
                    <FaTachometerAlt className="w-4 h-4 text-yellow-400" />
                    <span className="font-medium">Dashboard</span>
                  </Link>

                  {/* ADMIN PANEL LINK FOR MOBILE - Only show for super admin */}
                  {isSuperAdmin && (
                    <Link
                      to="/admin"
                      className="flex items-center space-x-3 px-3 py-2 text-purple-300 hover:text-purple-200 hover:bg-purple-500/10 rounded-lg transition-colors text-sm mt-2"
                      onClick={() => setIsOpen(false)}
                    >
                      <FaUserShield className="w-4 h-4 text-purple-400" />
                      <span className="font-medium">Admin Panel</span>
                    </Link>
                  )}

                  <button
                    onClick={async () => {
                      await signOut();
                      navigate('/');
                      setIsOpen(false);
                    }}
                    className="flex items-center space-x-3 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors w-full mt-2 text-sm"
                  >
                    <FaSignOutAlt className="w-4 h-4" />
                    <span className="font-medium">Logout</span>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Navbar;
